/**
 * Tracking Utilities
 *
 * Helper functions for working with tracking IDs and configurations
 */

import { TRACKING_CONFIG } from './config';

/**
 * Check if a tracking ID exists in the configuration
 */
export function isValidTrackingId(trackingId: string): boolean {
  return trackingId in TRACKING_CONFIG;
}

/**
 * Get all available tracking IDs
 */
export function getAllTrackingIds(): string[] {
  return Object.keys(TRACKING_CONFIG);
}

/**
 * Validate tracking implementation in development
 * Scans the DOM for data-mixpanel-id attributes and validates them
 * 
 * @returns Validation data with missing and unused tracking IDs
 */
export function validateTrackingImplementation(): {
  valid: boolean;
  missing: string[];
  unused: string[];
  totalConfigured: number;
  totalUsed: number;
} {
  if (typeof window === 'undefined') {
    return {
      valid: false,
      missing: [],
      unused: [],
      totalConfigured: 0,
      totalUsed: 0
    };
  }

  // Find all elements with data-mixpanel-id attributes
  const elements = document.querySelectorAll('[data-mixpanel-id]');
  const usedIds = Array.from(elements).map(el => 
    el.getAttribute('data-mixpanel-id')
  ).filter(Boolean) as string[];
  
  const uniqueUsedIds = [...new Set(usedIds)];
  const configuredIds = getAllTrackingIds();
  
  const missing = uniqueUsedIds.filter(id => !configuredIds.includes(id));
  const unused = configuredIds.filter(id => !uniqueUsedIds.includes(id));
  
  const valid = missing.length === 0;
  
  return {
    valid,
    missing,
    unused,
    totalConfigured: configuredIds.length,
    totalUsed: uniqueUsedIds.length
  };
}

/**
 * Infer the location of a tracking element based on its DOM context
 * This is based on the proven logic from the old implementation
 * 
 * @param element - The element being tracked
 * @returns The location string for the tracking event
 */
export function inferTrackingLocation(element: HTMLElement): string {
  if (typeof window === 'undefined') return 'Unknown';
  
  try {
    let node = element;
    let depth = 0;

    // Look for the nearest heading (h1, h2, h3, h4, h5, h6) in ancestors
    while (node && depth < 10) {
      const tagName = (node.tagName && node.tagName.toLowerCase()) || "";
      
      // Check if current node is a heading
      if (/^h[1-6]$/.test(tagName)) {
        const text = (node.textContent || node.innerText || "").trim();
        if (text) {
          return text.length > 60 ? text.substring(0, 60) + "..." : text;
        }
      }
      
      // Check for headings within current node
      const heading = node.querySelector && node.querySelector("h1, h2, h3, h4, h5, h6") as HTMLElement;
      if (heading) {
        const text = (heading.textContent || heading.innerText || "").trim();
        if (text) {
          return text.length > 60 ? text.substring(0, 60) + "..." : text;
        }
      }

      node = node.parentNode as HTMLElement;
      depth++;
    }

    return "Content Section";
  } catch (e) {
    return "Unknown";
  }
}

/**
 * Determine the location for a tracking event based on element context
 * Uses predefined locations for header/hero/cta, falls back to dynamic inference
 * 
 * @param element - The element being tracked
 * @param trackingId - The tracking ID to check for predefined location
 * @returns The location string for the tracking event
 */
export function determineTrackingLocation(element: HTMLElement, trackingId: string): string {
  if (typeof window === 'undefined') return 'Unknown';
  
  // Check for predefined locations first (header, hero, cta)
  const config = TRACKING_CONFIG[trackingId];
  if (config?.location) {
    const predefinedLocation = config.location.toLowerCase();
    if (predefinedLocation.includes('header') || 
        predefinedLocation.includes('hero') || 
        predefinedLocation.includes('cta')) {
      return config.location;
    }
  }
  
  // For other tracking IDs, use the proven inference logic
  return inferTrackingLocation(element);
}

/**
 * Log tracking validation report to console
 * Useful for development debugging
 */
export function logTrackingValidation(): void {
  if (typeof window === 'undefined') return;
  
  const validation = validateTrackingImplementation();
  
  // Emit a direct error for each missing tracking ID so it's obvious in dev tools
  if (validation.missing && validation.missing.length > 0) {
    validation.missing.forEach((id) => {
      console.error(`❌ Mixpanel: Tracking ID "${id}" is not configured. Please add it to lib/tracking/config.ts or use a valid tracking ID from lib/tracking/config.ts.`);
    });
  }
  
  if (!validation.valid) {
    console.warn('🚨 Mixpanel tracking has configuration issues. Check the report above.');
  }
}
